if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // LTX: "Add pack" forms carry data-pack-size. Record the line's quantity first
        // so a partial add can be detected and undone afterwards.
        const packSize = parseInt(this.dataset.packSize, 10) || 0;
        const variantId = formData.get('id');
        let quantityBefore = null;

        (packSize ? this.getLine(variantId) : Promise.resolve(undefined))
          .then((line) => {
            if (line !== undefined) quantityBefore = line ? line.quantity : 0;
            return fetch(`${routes.cart_add_url}`, config);
          })
          .then((response) => response.json())
          .then((response) =>
            packSize && quantityBefore !== null ? this.confirmFullPack(response, variantId, packSize, quantityBefore) : response
          )
          .then((response) => {
            if (response.status) {
              if (packSize && window.ltxPackNoticeHide) window.ltxPackNoticeHide();
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (packSize) {
              this.cart.renderContents(response, { silent: true });
              setTimeout(() => this.announcePack(), 100);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            // LTX: e.g. a 429 HTML page instead of JSON. Don't let an add fail silently.
            if (packSize) this.handleErrorMessage(this.dataset.packFailedMessage);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // Resolves to the cart line, null when the variant isn't in the cart, or undefined
      // when the cart couldn't be read (the pack check is then skipped).
      getLine(variantId) {
        return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => cart.items.find((item) => String(item.variant_id) === String(variantId)) || null)
          .catch(() => undefined);
      }

      // Short on stock, Shopify adds what's left even when it answers with an error. A
      // partial pack would fail the minimum at the cart, so put the line back as it was.
      confirmFullPack(response, variantId, packSize, quantityBefore) {
        const short = { status: 422, description: this.dataset.packShortMessage };
        return this.getLine(variantId).then((line) => {
          if (line === undefined) return response;
          const quantityNow = line ? line.quantity : 0;
          if (quantityNow - quantityBefore === packSize) return response;
          if (quantityNow === quantityBefore) return response.status ? short : response;
          const config = fetchConfig('json');
          config.body = JSON.stringify({ id: line.key, quantity: quantityBefore });
          return fetch(`${routes.cart_change_url}.js`, config)
            .catch(() => null)
            .then(() => short);
        });
      }

      announcePack() {
        if (!window.ltxPackAdded) return;
        const total = this.cart && this.cart.querySelector('.totals__total-value');
        window.ltxPackAdded({
          title: this.dataset.packTitle,
          pack: this.dataset.packLabel,
          total: total ? total.textContent.trim() : '',
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
